// src/pages/Loads/ManifestsIndex.tsx
/**
 * Timber Management System — Loads › Manifests (index)
 *
 * Features
 * • Filters: Search (container/seal/voyage/port/site), Status (multi), Date range (ETA or Created), density toggle
 * • Saved Views via <SavedViews storageKey="opts:manifestViews:v1" />
 * • KPIs: Containers, Total Gross/Tare/Net (tabular, right-aligned), quick drill to filtered
 * • Bulk actions: Select visible, Mark Status (Draft/Ready/Shipped/Delivered/Closed), Export JSON, Delete (Admin/Dispatcher)
 * • Per-row actions: Info/Edit (Drawer), Open Detail (/loads/manifest/:container), Export JSON
 * • Data persistence: localStorage under loads:manifests:v1 (safe normalization)
 * • Table: zebra + sticky header; numeric columns right-aligned with tabular numerals; CSV export via DataTable
 * • Audit + notifications wired
 *
 * Assumptions
 * • Route /loads/manifest/:container exists for detail (we link the container cell).
 * • Common components exist per brief: AppShell, DataTable, Drawer, Skeleton, EmptyState, MultiSelect, SavedViews.
 * • Notifications API: useNotifications().push({ title, body?, level? }).
 */

import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import AppShell from '@/components/AppShell'
import DataTable, { Col } from '@/components/DataTable'
import Drawer from '@/components/Drawer'
import Skeleton from '@/components/Skeleton'
import EmptyState from '@/components/EmptyState'
import MultiSelect from '@/components/MultiSelect'
import SavedViews from '@/components/SavedViews'
import { useNotifications } from '@/lib/notifications'
import { get, set } from '@/lib/storage'
import { logAudit } from '@/lib/audit'
import {
  CalendarRange,
  CheckCheck,
  CircleCheckBig,
  PackageCheck,
  Ship,
  FileJson,
  Trash2,
  Info as InfoIcon,
  CheckSquare, Square,
  Download,
  Save,
} from 'lucide-react'

type Role = 'Admin'|'Dispatcher'|'Loader'|'Trucker'|'Accountant'
const actorRole: Role = get<Role>('role','Admin' as Role)

type DatePreset = 'all'|'last7'|'last30'|'last90'|'ytd'|'custom'
type Status = 'Draft'|'Ready'|'Shipped'|'Delivered'|'Closed'

type Manifest = {
  id: string
  container: string
  seal?: string
  voyage?: string
  vessel?: string
  port?: string
  siteId?: string
  siteName?: string
  mill?: string
  gross?: number
  tare?: number
  net?: number
  fumigation?: boolean
  eta?: string // ISO (date)
  etd?: string // ISO (date)
  status: Status
  notes?: string
  createdAt: string // ISO
  updatedAt?: string
}

type Filters = {
  q: string
  statuses: Status[]
  datePreset: DatePreset
  dateMode: 'eta'|'created'
  from?: string
  to?: string
  density: 'comfort'|'compact'
}

const DATA_KEY = 'loads:manifests:v1'
const VIEWS_KEY = 'opts:manifestViews:v1'
const TBL_KEY = 'tbl:manifests:v1'

const fmtNum = (n?: number) => Number(n || 0).toLocaleString()
const cx = (...xs: Array<string | false | null | undefined>) => xs.filter(Boolean).join(' ')

function uid() { return (globalThis.crypto && 'randomUUID' in globalThis.crypto) ? (crypto as any).randomUUID() : Math.random().toString(36).slice(2) }
function startOfYearISO() { const d = new Date(); d.setMonth(0,1); d.setHours(0,0,0,0); return d.toISOString().slice(0,10) }
function todayISO()       { const d = new Date(); d.setHours(23,59,59,999); return d.toISOString().slice(0,10) }
function addDays(base: Date, days: number) { const d = new Date(base); d.setDate(d.getDate()+days); return d }
function presetToRange(preset: DatePreset): { from?: string; to?: string } {
  const today = new Date()
  switch (preset) {
    case 'last7':  return { from: addDays(today, -6).toISOString().slice(0,10),  to: todayISO() }
    case 'last30': return { from: addDays(today, -29).toISOString().slice(0,10), to: todayISO() }
    case 'last90': return { from: addDays(today, -89).toISOString().slice(0,10), to: todayISO() }
    case 'ytd':    return { from: startOfYearISO(), to: todayISO() }
    case 'all':
    default:       return { from: undefined, to: undefined }
  }
}
function withinRange(iso?: string, from?: string, to?: string) {
  if (!iso) return false
  const d = iso.slice(0,10)
  if (from && d < from) return false
  if (to && d > to) return false
  return true
}
function normalize(list: any[]): Manifest[] {
  return (list || []).map((m: any) => ({
    id: m.id || uid(),
    container: String(m.container || ''),
    seal: m.seal || '',
    voyage: m.voyage || '',
    vessel: m.vessel || '',
    port: m.port || '',
    siteId: m.siteId || '',
    siteName: m.siteName || '',
    mill: m.mill || '',
    gross: Number(m.gross || 0),
    tare: Number(m.tare || 0),
    net: Number(m.net ?? (Number(m.gross || 0) - Number(m.tare || 0))),
    fumigation: !!m.fumigation,
    eta: m.eta || '',
    etd: m.etd || '',
    status: (['Draft','Ready','Shipped','Delivered','Closed'].includes(m.status) ? m.status : 'Draft') as Status,
    notes: m.notes || '',
    createdAt: m.createdAt || new Date().toISOString(),
    updatedAt: m.updatedAt || m.createdAt || new Date().toISOString(),
  }))
}

export default function ManifestsIndex() {
  const { push } = useNotifications()

  // data
  const [manifests, setManifests] = useState<Manifest[]>(() => normalize(get<Manifest[]>(DATA_KEY, [])))
  const [loading, setLoading] = useState(false)

  // filters
  const [filters, setFilters] = useState<Filters>(() => ({
    q: '',
    statuses: [],
    datePreset: 'all',
    dateMode: 'eta',
    ...presetToRange('all'),
    density: (get<'comfort'|'compact'>('ui:table:density','compact') || 'compact'),
  }))

  // selection + drawer
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [editing, setEditing] = useState<Manifest | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  useEffect(() => { set('ui:table:density', filters.density) }, [filters.density])

  // helpers
  function persist(next: Manifest[]) { setManifests(next); set(DATA_KEY, next) }
  function openCreate() {
    const m: Manifest = {
      id: uid(),
      container: '',
      status: 'Draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    setEditing(m); setDrawerOpen(true)
  }
  function openEdit(m: Manifest) { setEditing({ ...m }); setDrawerOpen(true) }
  function saveEdit() {
    if (!editing) return
    const item = { ...editing, net: Number(editing.net ?? (Number(editing.gross||0)-Number(editing.tare||0))), updatedAt: new Date().toISOString() }
    const next = upsertById(manifests, item)
    persist(next)
    setDrawerOpen(false)
    push({ title: 'Manifest saved', body: item.container })
    logAudit({ action: 'manifests:save', id: item.id, container: item.container }, actorRole)
  }

  // seed samples
  function addSamples() {
    const today = Date.now()
    const make = (i: number): Manifest => ({
      id: uid(),
      container: `MSCU${(4000000 + i).toString()}`,
      seal: `SEAL${1000+i}`,
      voyage: `VOY-${80 + i}`,
      vessel: 'Ever Woods',
      port: i % 2 ? 'SAV' : 'CHS',
      siteName: i % 2 ? 'Pine Flats' : 'North Ridge',
      mill: i % 3 ? 'TimCo Mill A' : 'TimCo Mill B',
      gross: 84000 - i*300,
      tare: 32000,
      net: undefined as unknown as number,
      fumigation: i % 4 === 0,
      eta: new Date(today + i*86400000).toISOString(),
      etd: new Date(today - i*86400000).toISOString(),
      status: (i % 5 === 0 ? 'Delivered' : i % 3 === 0 ? 'Shipped' : i % 2 === 0 ? 'Ready' : 'Draft') as Status,
      notes: i % 2 ? '' : 'Check seal on arrival',
      createdAt: new Date(today - i*43200000).toISOString(),
      updatedAt: new Date(today - i*3600000).toISOString(),
    })
    const next = normalize([make(1),make(2),make(3),make(4),make(5)]).concat(manifests)
    persist(next)
    push({ title: 'Added sample manifests', body: '5 demo rows' })
    logAudit({ action: 'manifests:samples:add', count: 5 }, actorRole)
  }

  // filtering
  const activeRange = useMemo(() => {
    const { datePreset, from, to } = filters
    return datePreset === 'custom' ? { from, to } : presetToRange(datePreset)
  }, [filters.datePreset, filters.from, filters.to])

  const visible = useMemo(() => {
    const term = (filters.q || '').trim().toLowerCase()
    const { from, to } = activeRange
    return manifests.filter((m) => {
      if (filters.statuses.length && !filters.statuses.includes(m.status)) return false
      if (from || to) {
        const keyDate = filters.dateMode === 'eta' ? m.eta : m.createdAt
        if (!withinRange(keyDate, from, to)) return false
      }
      if (!term) return true
      const hay = [
        m.container, m.seal, m.voyage, m.vessel, m.port, m.siteName, m.mill, m.notes
      ].filter(Boolean).join(' ').toLowerCase()
      return hay.includes(term)
    })
  }, [manifests, filters, activeRange])

  const kpis = useMemo(() => {
    const totals = visible.reduce((acc, m) => {
      acc.count++
      acc.gross += Number(m.gross || 0)
      acc.tare  += Number(m.tare || 0)
      acc.net   += Number(m.net ?? (Number(m.gross||0) - Number(m.tare||0)))
      return acc
    }, { count: 0, gross: 0, tare: 0, net: 0 })
    return totals
  }, [visible])

  // selection
  function toggleSelect(id: string) {
    setSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next })
  }
  function toggleSelectVisible() {
    setSelected(prev => {
      const next = new Set(prev)
      const ids = visible.map(v => v.id)
      const all = ids.length > 0 && ids.every(id => next.has(id))
      if (all) ids.forEach(id => next.delete(id)); else ids.forEach(id => next.add(id))
      return next
    })
  }
  const anySelected = selected.size > 0
  const canManage = actorRole === 'Admin' || actorRole === 'Dispatcher'

  // bulk actions
  function bulkSetStatus(nextStatus: Status) {
    if (!canManage || !anySelected) return
    const ids = new Set(selected)
    const next = manifests.map(m => ids.has(m.id) ? { ...m, status: nextStatus, updatedAt: new Date().toISOString() } : m)
    persist(next); setSelected(new Set())
    push({ title: 'Status updated', body: `${ids.size} manifest(s) → ${nextStatus}` })
    logAudit({ action: 'manifests:bulkStatus', to: nextStatus, count: ids.size }, actorRole)
  }
  function bulkDelete() {
    if (!canManage || !anySelected) return
    if (!confirm(`Delete ${selected.size} selected manifest(s)?`)) return
    const ids = new Set(selected)
    const next = manifests.filter(m => !ids.has(m.id))
    persist(next); setSelected(new Set())
    push({ title: 'Deleted', body: `${ids.size} manifest(s)` })
    logAudit({ action: 'manifests:bulkDelete', count: ids.size }, actorRole)
  }
  function exportSelectedJSON() {
    const ids = new Set(selected)
    const items = manifests.filter(m => ids.has(m.id))
    if (!items.length) return
    const blob = new Blob([JSON.stringify(items, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = `manifests_${new Date().toISOString().slice(0,10)}.json`
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
    push({ title: 'Exported JSON', body: `${items.length} manifest(s)` })
  }

  // table
  const cols: Col[] = [
    { key: 'select',    label: '' },
    { key: 'container', label: 'Container' },
    { key: 'seal',      label: 'Seal' },
    { key: 'voyage',    label: 'Voyage' },
    { key: 'port',      label: 'Port' },
    { key: 'gross',     label: 'Gross (lbs)' },
    { key: 'tare',      label: 'Tare (lbs)' },
    { key: 'net',       label: 'Net (lbs)' },
    { key: 'status',    label: 'Status' },
    { key: 'actions',   label: 'Actions' },
  ]

  const rowsForTable = visible.map((m) => {
    const isSel = selected.has(m.id)
    const badgeClass =
      m.status === 'Delivered' ? 'bg-emerald-100 text-emerald-800' :
      m.status === 'Shipped'   ? 'bg-blue-100 text-blue-800' :
      m.status === 'Ready'     ? 'bg-amber-100 text-amber-800' :
      m.status === 'Closed'    ? 'bg-slate-300 text-slate-800' : 'bg-slate-100 text-slate-700'

    return {
      select: (
        <div className="pl-2 min-w-[2.5rem]">
          <button className="btn-secondary px-2 py-1" aria-pressed={isSel} onClick={() => toggleSelect(m.id)}>
            {isSel ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
          </button>
        </div>
      ),
      container: (
        <div className="pl-3 pr-2 min-w-[14rem]">
          <Link className="underline block truncate" to={`/loads/manifest/${encodeURIComponent(m.container)}`} title="Open manifest detail">
            {m.container || '—'}
          </Link>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {m.vessel ? `${m.vessel}` : ''}{m.vessel && m.voyage ? ' • ' : ''}{m.voyage || ''}
          </div>
        </div>
      ),
      seal:    <div className="min-w-[10rem] truncate" title={m.seal || ''}>{m.seal || '—'}</div>,
      voyage:  <div className="min-w-[10rem] truncate" title={m.voyage || ''}>{m.voyage || '—'}</div>,
      port:    <div className="min-w-[8rem]">{m.port || '—'}</div>,
      gross:   <div className="text-right tabular-nums min-w-[8rem]">{fmtNum(m.gross)}</div>,
      tare:    <div className="text-right tabular-nums min-w-[8rem]">{fmtNum(m.tare)}</div>,
      net:     <div className="text-right tabular-nums min-w-[8rem]">{fmtNum(m.net)}</div>,
      status:  <div className="min-w-[8rem]"><span className={cx('badge', badgeClass)}>{m.status}</span></div>,
      actions: (
        <div className="flex justify-end gap-2 pr-2 min-w-[12rem]">
          <button className="btn-secondary px-2 py-1" title="Info / Edit" onClick={() => openEdit(m)}>
            <InfoIcon className="w-4 h-4" />
          </button>
          <button
            className="btn-secondary px-2 py-1"
            title="Export JSON"
            onClick={() => {
              const blob = new Blob([JSON.stringify(m, null, 2)], { type: 'application/json' })
              const url = URL.createObjectURL(blob)
              const a = document.createElement('a'); a.href = url; a.download = `manifest_${(m.container || m.id).replace(/\s+/g,'_')}.json`
              document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
              logAudit({ action: 'manifests:export:json', id: m.id }, actorRole)
            }}
          >
            <FileJson className="w-4 h-4" />
          </button>
          {canManage && (
            <button
              className="btn-danger px-2 py-1"
              title="Delete"
              onClick={() => {
                if (!confirm(`Delete manifest ${m.container}?`)) return
                const next = manifests.filter(x => x.id !== m.id); persist(next)
                push({ title: 'Deleted', body: m.container }); logAudit({ action: 'manifests:delete', id: m.id }, actorRole)
              }}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    }
  })

  const densityClass = filters.density === 'compact' ? 'density-compact' : ''

  return (
    <AppShell title="Manifests">
      {/* Filters / Views / Density */}
      <div className="card mb-3">
        <div className="card-header flex items-center justify-between">
          <span>Container Manifests</span>
          <div className="flex items-center gap-2">
            <SavedViews<Filters>
              storageKey={VIEWS_KEY}
              currentFilters={filters}
              onLoad={(next) => setFilters(next)}
            />
            <div className="popover relative">
              <details>
                <summary className="btn-secondary px-3 py-1.5">
                  Density: {filters.density === 'compact' ? 'Compact' : 'Comfort'}
                </summary>
                <div className="popover p-2 mt-2 w-48">
                  <button className="btn-secondary w-full mb-2" onClick={() => setFilters(f => ({ ...f, density: 'comfort' }))}>Comfort</button>
                  <button className="btn-secondary w-full"     onClick={() => setFilters(f => ({ ...f, density: 'compact' }))}>Compact</button>
                </div>
              </details>
            </div>
          </div>
        </div>

        <div className="card-body grid lg:grid-cols-3 gap-3 items-start">
          {/* Search + statuses */}
          <div className="space-y-3">
            <div>
              <label className="text-sm">Search</label>
              <input
                className="input"
                placeholder="Search container, seal, voyage, port, site…"
                value={filters.q}
                onChange={(e) => setFilters(f => ({ ...f, q: e.target.value }))}
              />
              <div className="text-xs muted mt-1">
                Showing <span className="tabular-nums">{visible.length.toLocaleString()}</span> manifest{visible.length===1?'':'s'}.
              </div>
            </div>

            <div>
              <label className="text-sm">Status</label>
              <MultiSelect
                options={['Draft','Ready','Shipped','Delivered','Closed']}
                value={filters.statuses}
                onChange={(vals) => setFilters(f => ({ ...f, statuses: vals as Status[] }))}
                placeholder="All statuses"
                storageKey="opts:manifestStatuses:v1"
              />
            </div>
          </div>

          {/* Date + KPIs */}
          <div className="lg:col-span-2 space-y-3">
            <div className="grid sm:grid-cols-5 gap-3 items-end">
              <div className="sm:col-span-2">
                <label className="text-sm">Date Mode</label>
                <select
                  className="select w-full"
                  value={filters.dateMode}
                  onChange={(e) => setFilters(f => ({ ...f, dateMode: (e.target.value as 'eta'|'created') }))}
                >
                  <option value="eta">ETA</option>
                  <option value="created">Created</option>
                </select>
              </div>
              <div>
                <label className="text-sm">Range</label>
                <div className="flex items-center gap-2">
                  <CalendarRange className="w-4 h-4 muted" />
                  <select
                    className="select w-full"
                    value={filters.datePreset}
                    onChange={(e) => {
                      const p = e.target.value as DatePreset
                      const r = p === 'custom' ? {} : presetToRange(p)
                      setFilters((f) => ({ ...f, datePreset: p, ...r }))
                    }}
                  >
                    <option value="all">All time</option>
                    <option value="last7">Last 7 days</option>
                    <option value="last30">Last 30 days</option>
                    <option value="last90">Last 90 days</option>
                    <option value="ytd">Year to date</option>
                    <option value="custom">Custom…</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm">From</label>
                <input
                  type="date"
                  className="input"
                  value={filters.datePreset === 'custom' ? (filters.from || '') : (presetToRange(filters.datePreset).from || '')}
                  onChange={(e) => setFilters((f) => ({ ...f, datePreset: 'custom', from: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm">To</label>
                <input
                  type="date"
                  className="input"
                  value={filters.datePreset === 'custom' ? (filters.to || '') : (presetToRange(filters.datePreset).to || '')}
                  onChange={(e) => setFilters((f) => ({ ...f, datePreset: 'custom', to: e.target.value }))}
                />
              </div>
            </div>

            {/* KPIs */}
            <div className="grid sm:grid-cols-4 gap-3">
              <div className="card p-3 bg-[var(--surface-1)]">
                <div className="text-xs muted">Containers</div>
                <div className="text-lg font-semibold tabular-nums">{kpis.count.toLocaleString()}</div>
              </div>
              <div className="card p-3 bg-[var(--surface-1)]">
                <div className="text-xs muted">Total Gross</div>
                <div className="text-lg font-semibold tabular-nums">{fmtNum(kpis.gross)}</div>
              </div>
              <div className="card p-3 bg-[var(--surface-1)]">
                <div className="text-xs muted">Total Tare</div>
                <div className="text-lg font-semibold tabular-nums">{fmtNum(kpis.tare)}</div>
              </div>
              <div className="card p-3 bg-[var(--surface-1)]">
                <div className="text-xs muted">Total Net</div>
                <div className="text-lg font-semibold tabular-nums">{fmtNum(kpis.net)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk bar */}
      {anySelected && (
        <div className="card mb-2">
          <div className="card-body flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button className="btn-secondary px-3 py-1.5" onClick={toggleSelectVisible}>Select visible</button>
              <div className="text-sm"><span className="tabular-nums">{selected.size}</span> selected</div>
            </div>
            <div className="flex items-center gap-2">
              <button className="btn-secondary" onClick={() => bulkSetStatus('Ready')} title="Mark Ready">
                <CheckCheck className="w-4 h-4" /> Ready
              </button>
              <button className="btn-secondary" onClick={() => bulkSetStatus('Shipped')} title="Mark Shipped">
                <Ship className="w-4 h-4" /> Shipped
              </button>
              <button className="btn-secondary" onClick={() => bulkSetStatus('Delivered')} title="Mark Delivered">
                <PackageCheck className="w-4 h-4" /> Delivered
              </button>
              <button className="btn-secondary" onClick={exportSelectedJSON} title="Export selected as JSON">
                <Download className="w-4 h-4" /> Export JSON
              </button>
              {canManage && (
                <button className="btn-danger" onClick={bulkDelete} title="Delete selected">
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <Skeleton className="h-64" />
      ) : rowsForTable.length === 0 ? (
        <EmptyState title="No manifests yet">
          Create one or{' '}
          <button className="underline" onClick={addSamples}>add sample manifests</button>.
          <div className="mt-3">
            <button className="btn" onClick={openCreate}>New Manifest</button>
          </div>
        </EmptyState>
      ) : (
        <div className={densityClass}>
          <DataTable columns={cols} rows={rowsForTable} filename="manifests.csv" storageKey={TBL_KEY} />
        </div>
      )}

      {/* Edit Drawer */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={editing?.container || 'Manifest'}>
        {editing && (
          <div className="drawer-panel space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm">Container #</label>
                <input className="input" value={editing.container} onChange={(e) => setEditing({ ...editing, container: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">Seal #</label>
                <input className="input" value={editing.seal || ''} onChange={(e) => setEditing({ ...editing, seal: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">Voyage</label>
                <input className="input" value={editing.voyage || ''} onChange={(e) => setEditing({ ...editing, voyage: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">Vessel</label>
                <input className="input" value={editing.vessel || ''} onChange={(e) => setEditing({ ...editing, vessel: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">Port</label>
                <input className="input" value={editing.port || ''} onChange={(e) => setEditing({ ...editing, port: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">Site</label>
                <input className="input" value={editing.siteName || ''} onChange={(e) => setEditing({ ...editing, siteName: e.target.value })} placeholder="(optional quick text)" />
              </div>
              <div>
                <label className="text-sm">Mill</label>
                <input className="input" value={editing.mill || ''} onChange={(e) => setEditing({ ...editing, mill: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-3 sm:col-span-2">
                <div>
                  <label className="text-sm">Gross (lbs)</label>
                  <input type="number" className="input" value={editing.gross ?? 0} onChange={(e) => setEditing({ ...editing, gross: Number(e.target.value || 0) })} />
                </div>
                <div>
                  <label className="text-sm">Tare (lbs)</label>
                  <input type="number" className="input" value={editing.tare ?? 0} onChange={(e) => setEditing({ ...editing, tare: Number(e.target.value || 0) })} />
                </div>
                <div>
                  <label className="text-sm">Net (lbs)</label>
                  <input type="number" className="input" value={editing.net ?? 0} onChange={(e) => setEditing({ ...editing, net: Number(e.target.value || 0) })} />
                </div>
              </div>
              <div>
                <label className="text-sm">ETA (date)</label>
                <input type="date" className="input" value={editing.eta ? editing.eta.slice(0,10) : ''} onChange={(e) => setEditing({ ...editing, eta: e.target.value ? new Date(e.target.value).toISOString() : '' })} />
              </div>
              <div>
                <label className="text-sm">ETD (date)</label>
                <input type="date" className="input" value={editing.etd ? editing.etd.slice(0,10) : ''} onChange={(e) => setEditing({ ...editing, etd: e.target.value ? new Date(e.target.value).toISOString() : '' })} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm">Notes</label>
                <input className="input" value={editing.notes || ''} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
            </div>

            <div className="flex gap-2">
              <button className="btn-secondary" onClick={() => setDrawerOpen(false)}>Close</button>
              <div className="flex-1" />
              {editing.status !== 'Closed' && (
                <>
                  <button className="btn-secondary" onClick={() => setEditing(e => e ? { ...e, status: 'Ready' } : e)}>
                    <CheckCheck className="w-4 h-4" /> Ready
                  </button>
                  <button className="btn-secondary" onClick={() => setEditing(e => e ? { ...e, status: 'Shipped' } : e)}>
                    <Ship className="w-4 h-4" /> Shipped
                  </button>
                  <button className="btn-secondary" onClick={() => setEditing(e => e ? { ...e, status: 'Delivered' } : e)}>
                    <CircleCheckBig className="w-4 h-4" /> Delivered
                  </button>
                </>
              )}
              <button className="btn" onClick={saveEdit}>
                <Save className="w-4 h-4" /> Save
              </button>
            </div>
          </div>
        )}
      </Drawer>

      {/* Footer toolbar */}
      <div className="mt-3 flex items-center justify-between">
        <div className="muted text-sm">Totals reflect current filters.</div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary" onClick={addSamples}>
            <Download className="w-4 h-4" /> Add Samples
          </button>
          <button className="btn" onClick={openCreate}>New Manifest</button>
        </div>
      </div>
    </AppShell>
  )
}

/* ---------------- helpers ---------------- */
function upsertById(list: Manifest[], item: Manifest) {
  const idx = list.findIndex(x => x.id === item.id)
  if (idx === -1) return [item, ...list]
  const next = [...list]; next[idx] = item; return next
}
